import { createAsyncThunk } from '@reduxjs/toolkit';
import adserver from 'services/adserver';
import { withErrorHandling } from 'store/wrappers';
import { REDUCER_KEY } from './proposals.consts';
import { ProposalParams, ProposalListParams } from './proposals.types';
import { selectCurrentCampaignId } from 'store/campaigns/campaigns.selectors';
import { selectCurrentProposal } from './proposals.selectors';
import { fetchAllFlightsOfProposal } from 'store/flights';
import {
  //normalizeDateForApi,
  normalizeDateFromApi,
} from 'helpers/datetimeHelpers';

function normalizeApiReponse(data: ProposalParams) {
  if (!data.productSnapshot.timedSegments) {
    return data;
  }
  const segments = data.productSnapshot.timedSegments.map(segment => ({
    ...segment,
    startDate: normalizeDateFromApi(segment.startDate),
    endDate: segment.endDate ? normalizeDateFromApi(segment.endDate) : null,
  }));
  return {
    ...data,
    productSnapshot: { ...data.productSnapshot, timedSegments: segments },
  };
}

/* function normalizeApiRequest(data: ProposalParams) {
  if (!data.productSnapshot.timedSegments) {
    return data;
  }
  const segments = data.productSnapshot.timedSegments.map(segment => ({
    ...segment,
    startDate: normalizeDateForApi(segment.startDate),
    endDate: segment.endDate ? normalizeDateForApi(segment.endDate) : null,
  }));
  return {
    ...data,
    productSnapshot: { ...data.productSnapshot, timedSegments: segments },
  };
}
 */
const fetchByIdHandler = async ({ proposalId }: { proposalId: number }) => {
  const proposal = await adserver({
    url: `/proposal/${proposalId}`,
    headers: {},
  });

  const surcharges = await adserver({
    url: `/proposal/${proposalId}/surcharges`,
    headers: {},
  });

  return { ...proposal.data, surcharges: surcharges?.data };
};

export const fetchAllProposals = createAsyncThunk(
  REDUCER_KEY + '/fetchAll',
  withErrorHandling(async () => {
    const response = await adserver({ url: '/proposals', headers: {} });
    return response.data.map((proposal: ProposalListParams) => proposal);
  }),
);

export const fetchById = createAsyncThunk(
  REDUCER_KEY + '/get',
  withErrorHandling(async ({ proposalId }: { proposalId: number }) => {
    const response = await adserver({
      url: `/proposal/${proposalId}`,
      headers: {},
    });
    const surcharges = await adserver({
      url: `/proposal/${proposalId}/surcharges`,
      headers: {},
    });
    const proposal = normalizeApiReponse(response.data);
    return {
      ...proposal,
      surcharges: surcharges?.data,
    };
  }),
);

export const setCurrentProposal = createAsyncThunk(
  REDUCER_KEY + '/current',
  withErrorHandling(async (proposalId: number, thunkAPI: any) => {
    if (!proposalId) return;
    const proposal = await fetchByIdHandler({ proposalId });
    thunkAPI.dispatch(fetchAllFlightsOfProposal({ proposalId }));
    return normalizeApiReponse(proposal);
  }),
);

export const fetchProposalsOfCampaign = createAsyncThunk(
  REDUCER_KEY + '/fetchProposalsOfCampaign',
  withErrorHandling(async ({ campaignId }: { campaignId: number }) => {
    const response = await adserver({
      url: `/campaign/${campaignId}/proposals`,
      headers: {},
    });
    return response.data.map((deal: ProposalParams) =>
      normalizeApiReponse(deal),
    );
  }),
);

export const fetchAllProposalsForCurrentCampaign = createAsyncThunk(
  REDUCER_KEY + '/',
  withErrorHandling(async (_: any, thunkApi: any) => {
    const campaignId = selectCurrentCampaignId(thunkApi.getState());
    const response = await adserver({
      url: `/campaign/${campaignId}/proposals`,
      headers: {},
    });

    return response.data;
  }),
);

export const fetchProposalsOfBasket = createAsyncThunk(
  REDUCER_KEY + '/fetchProposalsOfBasket',
  withErrorHandling(async ({ basketId }: { basketId: number }) => {
    const response = await adserver({
      url: `/basket/${basketId}/proposals`,
      headers: {},
    });

    return response.data.map((deal: ProposalParams) => ({
      ...normalizeApiReponse(deal),
      basket: basketId,
    }));
  }),
);

export const create = createAsyncThunk(
  REDUCER_KEY + '/create',
  withErrorHandling(async (products: number[], thunkApi: any) => {
    const campaignId = selectCurrentCampaignId(thunkApi.getState());
    if (!campaignId) throw new Error('no campaignId');
    const response = await adserver({
      url: `/campaign/${campaignId}/proposal/`,
      method: 'POST',
      headers: {},
      data: { products },
    });

    return response.data.map((deal: ProposalParams) =>
      normalizeApiReponse(deal),
    );
  }),
);

export const submit = createAsyncThunk(
  REDUCER_KEY + '/submit',
  withErrorHandling(async (data: ProposalParams) => {
    const { id: proposalId } = data;
    const requsetdata = data;
    const response = await adserver({
      url: `/proposal/${proposalId}/submit`,
      method: 'POST',
      headers: {},
      data: requsetdata,
    });
    return normalizeApiReponse(response.data);
  }),
);

export const edit = createAsyncThunk(
  REDUCER_KEY + '/edit',
  withErrorHandling(async (data: ProposalParams) => {
    const { id: proposalId } = data;
    const requsetdata = data;
    const response = await adserver({
      url: `/proposal/${proposalId}/edit`,
      method: 'POST',
      headers: {},
      data: requsetdata,
    });
    return normalizeApiReponse(response.data);
  }),
);

export const accept = createAsyncThunk(
  REDUCER_KEY + '/accept',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/accept`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const rectify = createAsyncThunk(
  REDUCER_KEY + '/rectify',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/rectify`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const generateOrder = createAsyncThunk(
  REDUCER_KEY + '/generate_order',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/generate_order`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const renegotiate = createAsyncThunk(
  REDUCER_KEY + '/renegotiate',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/renegotiate`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const cancel = createAsyncThunk(
  REDUCER_KEY + '/cancel',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/cancel`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const resume = createAsyncThunk(
  REDUCER_KEY + '/resume',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/resume`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const suspend = createAsyncThunk(
  REDUCER_KEY + '/suspend',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/suspend`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const acknowledge = createAsyncThunk(
  REDUCER_KEY + '/acknowledge_notification',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/acknowledge_notification/`,
      method: 'POST',
      headers: {},
    });
    return response.data;
  }),
);

export const removeAllProposals = (): { type: string } => ({
  type: REDUCER_KEY + '/removeAll',
});

export const fetchProposalFormRate = createAsyncThunk(
  REDUCER_KEY + '/fetchFormRate',
  withErrorHandling(
    async ({
      proposalId,
      formData,
    }: {
      proposalId: number;
      formData: ProposalParams;
    }) => {
      const response = await adserver({
        url: `/proposal/${proposalId}/rate`,
        method: 'POST',
        data: formData,
        timeout: 10000,
        headers: {},
      });
      return response;
    },
  ),
);

export const updateProposalViewTS = createAsyncThunk(
  REDUCER_KEY + '/updateProposalViewTS',
  withErrorHandling(
    async (
      { proposalId, ts }: { proposalId: number; ts: number },
      thunkAPI: any,
    ) => {
      await adserver({
        url: `/proposal/${proposalId}/markread`,
        method: 'POST',
        headers: {},
        data: {
          lastViewedChangeTS: ts,
        },
      });
      const proposal = selectCurrentProposal(thunkAPI.getState());

      return {
        ...proposal,
        readStatus: {
          ...proposal?.readStatus,
          lastViewedChangeTS: ts,
        },
      };
    },
  ),
);

export const updateProposalChatViewTS = createAsyncThunk(
  REDUCER_KEY + '/updateProposalChatViewTS',
  withErrorHandling(
    async (
      { proposalId, ts }: { proposalId: number; ts: number },
      thunkAPI: any,
    ) => {
      await adserver({
        url: `/proposal/${proposalId}/markread`,
        method: 'POST',
        headers: {},
        data: {
          lastViewedMessageTS: ts,
        },
      });
      const proposal = selectCurrentProposal(thunkAPI.getState());

      return {
        ...proposal,
        readStatus: {
          ...proposal?.readStatus,
          lastViewedMessageTS: ts,
        },
      };
    },
  ),
);

export const updateProposalAttachementViewTS = createAsyncThunk(
  REDUCER_KEY + '/updateProposalAttachementViewTS',
  withErrorHandling(
    async (
      { proposalId, ts }: { proposalId: number; ts: number },
      thunkAPI: any,
    ) => {
      await adserver({
        url: `/proposal/${proposalId}/markread`,
        method: 'POST',
        headers: {},
        data: {
          lastViewedAttachementChangeTS: ts,
        },
      });
      const proposal = selectCurrentProposal(thunkAPI.getState());

      return {
        ...proposal,
        readStatus: {
          ...proposal?.readStatus,
          lastViewedAttachementChangeTS: ts,
        },
      };
    },
  ),
);

export const updateProposalMaterialViewTS = createAsyncThunk(
  REDUCER_KEY + '/updateProposalMaterialViewTS',
  withErrorHandling(
    async (
      { proposalId, ts }: { proposalId: number; ts: number },
      thunkAPI: any,
    ) => {
      await adserver({
        url: `/proposal/${proposalId}/markread`,
        method: 'POST',
        headers: {},
        data: {
          lastViewedMaterialChangeTS: ts,
        },
      });
      const proposal = selectCurrentProposal(thunkAPI.getState());

      return {
        ...proposal,
        readStatus: {
          ...proposal?.readStatus,
          lastViewedMaterialChangeTS: ts,
        },
      };
    },
  ),
);

export const updatePublisherSupervisor = createAsyncThunk(
  REDUCER_KEY + '/updatePublisherSupervisor',
  withErrorHandling(
    async ({
      publisherSupervisor,
      proposalId,
    }: {
      publisherSupervisor: string;
      proposalId: number;
    }) => {
      const { data } = await adserver({
        url: `/proposal/${proposalId}`,
        method: 'PATCH',
        data: { publisherSupervisor },
        headers: {},
      });
      return normalizeApiReponse(data);
    },
  ),
);

/**counterOffer */
export const acceptCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/acceptCounterOffer',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/counteroffer/accept`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

export const rejectCounterOffer = createAsyncThunk(
  REDUCER_KEY + '/rejectCounterOffer',
  withErrorHandling(async (proposalId: number) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/counteroffer/reject`,
      method: 'POST',
      headers: {},
    });
    return normalizeApiReponse(response.data);
  }),
);

/** watcher */
export const watchProposal = createAsyncThunk(
  REDUCER_KEY + '/watchProposal',
  withErrorHandling(
    async ({ proposalId, watch }: { proposalId: number; watch: boolean }) => {
      await adserver({
        url: `/proposal/${proposalId}/watch`,
        method: 'POST',
        data: { watch },
        headers: {},
      });

      return { id: proposalId, change: { isWatched: watch } };
    },
  ),
);
